import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../hd-content-language.service';
import { HdChironClickEvent, HdChironSide } from '../../hd-chiron.models';
import { HdPlanetInfoService } from '../../hd-planet-info.service';
import { HD_CHIRON_DISPLAY_CONFIG } from '../hd-planet-display-config';
import { HdSimpleExtraPlanetVm } from '../hd-simple-planet-grid/hd-simple-planet-grid.component';

interface ChironStatRowVm {
  readonly side: HdChironSide;
  readonly sideLetter: string;
  readonly position: string;
  readonly tooltip: string;
}

/**
 * Chiron (design + personality) als twee rijen rechtsonder in het
 * statistiekenblok van de volledige planetengrid: daar is in de grid zelf
 * geen plaats. Chiron is géén activatie (gedempte stijl), maar een klik opent
 * wel de gate-info van haar poort, zoals in de simpele grid.
 */
@Component({
  selector: 'hd-chiron-stats',
  templateUrl: './hd-chiron-stats.component.html',
  styleUrl: './hd-chiron-stats.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChironStatsComponent {
  readonly design = input<HdSimpleExtraPlanetVm | null>(null);
  readonly personality = input<HdSimpleExtraPlanetVm | null>(null);
  readonly chironClicked = output<HdChironClickEvent>();

  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly glyph = HD_CHIRON_DISPLAY_CONFIG.astronomiconChar;

  protected readonly name = computed(() =>
    this.planetInfo.planetInfoIn('chiron', this.lang())?.name || HD_CHIRON_DISPLAY_CONFIG.label);

  protected readonly rows = computed<readonly ChironStatRowVm[]>(() => {
    const rows: ChironStatRowVm[] = [];
    const add = (side: HdChironSide, vm: HdSimpleExtraPlanetVm | null): void => {
      if (vm == null) return;
      rows.push({
        side,
        sideLetter: side === 'design' ? 'D' : 'P',
        position: `${vm.gate}.${vm.line}`,
        tooltip: vm.tooltip,
      });
    };
    add('design', this.design());
    add('personality', this.personality());
    return rows;
  });

  protected onClick(side: HdChironSide, event: MouseEvent): void {
    this.chironClicked.emit({ side, origin: { x: event.clientX, y: event.clientY } });
  }
}
